import { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Globe, Hash, Trash2, Edit2, Search, MoreVertical, Plus } from 'lucide-react'
import { cn } from '@/lib/utils'
import { FacebookIcon, XIcon, ThreadsIcon } from '../../../shared/ui/BrandIcons'
import type {
  TopicList,
  TopicsQuery,
  CreateTopicListInput,
  UpdateTopicListInput,
} from '../model/contracts'
import { TopicListForm } from './TopicsForm'
import { Button } from '../../../shared/ui/Button'

interface TopicListListProps {
  topicLists: TopicList[]
  total: number
  query: TopicsQuery
  onSearchChange: (search: string) => void
  onPlatformChange: (platform: string) => void
  onSortChange: (field: string) => void
  onPageChange: (page: number) => void
  onCreate: (data: CreateTopicListInput) => void
  onUpdate: (data: UpdateTopicListInput) => void
  onDelete: (id: string) => void
}

const PlatformIcon = ({ platform }: { platform: string }) => {
  const p = platform.toLowerCase()
  if (p === 'facebook') return <FacebookIcon size={14} color="currentColor" />
  if (p === 'x' || p === 'twitter') return <XIcon size={14} color="currentColor" />
  if (p === 'threads') return <ThreadsIcon size={14} color="currentColor" />
  return <Globe size={14} />
}

export function TopicListList({
  topicLists,
  total,
  query,
  onSearchChange,
  onPlatformChange,
  onSortChange: _onSortChange,
  onPageChange,
  onCreate,
  onUpdate,
  onDelete,
}: TopicListListProps) {
  void _onSortChange // kept for interface compat — sort handled by chips
  const { t } = useTranslation()
  const [editingId, setEditingId] = useState<string | null>(null)
  const [showForm, setShowForm] = useState(false)

  const handleEdit = (topicList: TopicList) => {
    setEditingId(topicList.id)
    setShowForm(false)
  }

  const handleCancel = () => {
    setEditingId(null)
    setShowForm(false)
  }

  const handleSubmit = (data: CreateTopicListInput | UpdateTopicListInput) => {
    if ('id' in data) {
      onUpdate(data)
      setEditingId(null)
    } else {
      onCreate(data as CreateTopicListInput)
      setShowForm(false)
    }
  }

  const editingTopicList = editingId
    ? topicLists.find((kl) => kl.id === editingId)
    : undefined

  return (
    <div className="flex flex-col gap-4 sm:gap-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="m-0 text-lg font-bold text-foreground sm:text-2xl">
          {t('topics.list.title')}
        </h2>
        {!showForm && !editingId && (
          <Button
            onClick={() => setShowForm(true)}
            className="h-8 gap-1 px-3 text-xs sm:h-9 sm:px-4 sm:text-sm"
          >
            <Plus size={14} />
            <span className="hidden sm:inline">{t('topics.actions.addList')}</span>
            <span className="sm:hidden">{t('common.add', { defaultValue: 'Add' })}</span>
          </Button>
        )}
      </div>

      {/* Search bar */}
      <div className="relative">
        <Search
          size={16}
          className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground"
        />
        <input
          type="text"
          placeholder={t('topics.filters.searchPlaceholder')}
          value={query.search || ''}
          onChange={(e) => onSearchChange(e.target.value)}
          className="h-9 w-full rounded-full border border-border bg-muted/30 pl-10 pr-4 text-sm text-foreground outline-none backdrop-blur-md transition-all placeholder:text-muted-foreground/60 focus:border-primary/50 focus:ring-1 focus:ring-primary/20 sm:h-10"
        />
      </div>

      {/* Filter chips */}
      <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-none">
        {[
          { value: 'all', label: t('platforms.all') },
          { value: 'facebook', label: 'Facebook' },
          { value: 'x', label: 'X' },
          { value: 'threads', label: 'Threads' },
        ].map((chip) => (
          <button
            key={chip.value}
            type="button"
            onClick={() => onPlatformChange(chip.value)}
            className={cn(
              'shrink-0 rounded-full px-3 py-1.5 text-xs font-medium transition-all active:scale-95',
              (query.platform || 'all') === chip.value
                ? 'bg-chip-active-surface text-chip-active ring-1 ring-chip-active-ring'
                : 'bg-secondary/50 text-muted-foreground ring-1 ring-border/50 hover:bg-secondary',
            )}
          >
            {chip.label}
          </button>
        ))}
      </div>

      {/* Form */}
      {(showForm || editingTopicList) && (
        <div className="rounded-2xl border border-black/25 bg-card p-4 shadow-sm dark:border-white/[0.10] dark:shadow-none sm:p-6">
          <TopicListForm
            topicList={editingTopicList}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
          />
        </div>
      )}

      {/* List — card-centric */}
      {topicLists.length === 0 ? (
        <div className="flex flex-col items-center gap-5 rounded-2xl border border-black/25 bg-card px-6 py-20 text-center shadow-sm dark:border-white/[0.10] dark:shadow-none">
          <div className="empty-icon-pulse flex h-16 w-16 items-center justify-center rounded-2xl bg-nav-active-surface shadow-[0_0_20px_var(--nav-active-glow)]">
            <Hash size={28} className="text-primary" />
          </div>
          <div>
            <div className="text-lg font-semibold text-foreground">
              {t('topics.empty.title')}
            </div>
            <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
              {t('topics.empty.description')}
            </p>
          </div>
          <Button onClick={() => setShowForm(true)} className="btn-shimmer">
            {t('topics.actions.addList')}
          </Button>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {topicLists.map((topicList) => (
            <TopicCard
              key={topicList.id}
              topicList={topicList}
              onEdit={() => handleEdit(topicList)}
              onDelete={() => {
                if (confirm(t('topics.confirm.delete', { name: topicList.name }))) {
                  onDelete(topicList.id)
                }
              }}
            />
          ))}
        </div>
      )}

      {/* Pagination */}
      {total > (query.limit || 20) && (
        <div className="flex items-center justify-center gap-3">
          <Button
            variant="neutral"
            disabled={query.page === 1}
            onClick={() => onPageChange((query.page || 1) - 1)}
            className="h-8 px-3 text-xs"
          >
            {t('sources.prev')}
          </Button>
          <span className="text-xs text-muted-foreground">
            {t('sources.pageOf', {
              current: query.page,
              total: Math.ceil(total / (query.limit || 20)),
            })}
          </span>
          <Button
            variant="neutral"
            disabled={query.page === Math.ceil(total / (query.limit || 20))}
            onClick={() => onPageChange((query.page || 1) + 1)}
            className="h-8 px-3 text-xs"
          >
            {t('sources.next')}
          </Button>
        </div>
      )}
    </div>
  )
}

/* ── Topic Card ── */

function TopicCard({
  topicList,
  onEdit,
  onDelete,
}: {
  topicList: TopicList
  onEdit: () => void
  onDelete: () => void
}) {
  const { t } = useTranslation()
  const [menuOpen, setMenuOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!menuOpen) return
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [menuOpen])

  return (
    <article
      className={cn(
        'relative rounded-2xl border border-black/25 bg-card p-4 shadow-sm transition-all duration-200 lg:hover:scale-[1.01] lg:hover:shadow-md lg:hover:shadow-[var(--shadow-brand-highlight)] dark:border-white/[0.10] dark:shadow-none sm:p-5',
        !topicList.isActive && 'opacity-60',
      )}
    >
      {/* Top row: platform badge + kebab */}
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-nav-active-surface px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-primary">
            <PlatformIcon platform={topicList.platform} />
            {t(`platforms.${topicList.platform}`)}
          </span>
          {!topicList.isActive && (
            <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              {t('topics.status.inactive')}
            </span>
          )}
        </div>

        {/* Kebab menu */}
        <div ref={menuRef} className="relative">
          <button
            type="button"
            onClick={() => setMenuOpen((o) => !o)}
            className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-secondary/50 hover:text-foreground active:scale-95"
          >
            <MoreVertical size={16} />
          </button>
          {menuOpen && (
            <div className="absolute right-0 top-full z-10 mt-1 min-w-[120px] rounded-lg border border-border bg-popover py-1 shadow-lg">
              <button
                type="button"
                onClick={() => {
                  setMenuOpen(false)
                  onEdit()
                }}
                className="flex w-full items-center gap-2 px-3 py-2 text-sm text-foreground transition-colors hover:bg-secondary/50"
              >
                <Edit2 size={14} />
                {t('common.edit', { defaultValue: 'Edit' })}
              </button>
              <button
                type="button"
                onClick={() => {
                  setMenuOpen(false)
                  onDelete()
                }}
                className="flex w-full items-center gap-2 px-3 py-2 text-sm text-destructive transition-colors hover:bg-destructive/10"
              >
                <Trash2 size={14} />
                {t('common.delete', { defaultValue: 'Delete' })}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Title */}
      <h3 className="m-0 text-base font-semibold text-foreground sm:text-lg">
        {topicList.name}
      </h3>

      {/* Keywords */}
      {topicList.keywords.length > 0 ? (
        <div className="mt-2.5 flex flex-wrap gap-1.5">
          {topicList.keywords.map((keyword) => (
            <span
              key={keyword}
              className="rounded-md bg-secondary/60 px-2 py-0.5 text-[11px] font-medium text-muted-foreground ring-1 ring-border/30"
            >
              {keyword}
            </span>
          ))}
        </div>
      ) : (
        <p className="mt-2 text-xs italic text-muted-foreground">
          {t('topics.empty.keywords')}
        </p>
      )}

      {/* Meta */}
      <div className="mt-3 text-[11px] text-muted-foreground/60">
        {t('topics.meta.keywordsCount', { count: topicList.keywords.length })} ·{' '}
        {new Date(topicList.updatedAt).toLocaleDateString()}
      </div>
    </article>
  )
}
